import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Divider, Chip } from '@mui/material';
import { Popup } from 'react-leaflet';
import theme from '../../styles/theme';

/**
 * StatCard Component - Displays individual statistics
 */
const StatCard = ({ label, value, color = 'primary', trend = null }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 1.5,
      bgcolor: theme.colors.background.secondary,
      borderRadius: theme.borderRadius.md,
      minWidth: '80px',
      border: `1px solid ${theme.colors.border.light}`
    }}
  >
    <Typography 
      variant="h6" 
      sx={{ 
        fontWeight: 'bold',
        color: color === 'primary' ? theme.colors.primary[600] : 
               color === 'success' ? theme.colors.semantic.success :
               color === 'warning' ? theme.colors.semantic.warning :
               color === 'error' ? theme.colors.semantic.error :
               theme.colors.text.primary,
        mb: 0.5
      }}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
    <Typography 
      variant="caption" 
      sx={{ 
        color: theme.colors.text.secondary,
        textAlign: 'center',
        fontSize: '0.75rem'
      }}
    >
      {label}
    </Typography>
    {trend && (
      <Chip
        label={trend}
        size="small"
        sx={{
          mt: 0.5,
          height: '20px',
          fontSize: '0.65rem',
          bgcolor: trend.includes('+') ? theme.colors.semantic.successLight :
                   trend.includes('-') ? theme.colors.semantic.errorLight :
                   theme.colors.neutral[100]
        }}
      />
    )}
  </Box>
);

/**
 * RegionalBreakdown Component - Shows city-level data when zoomed in
 */
const RegionalBreakdown = ({ cities, countryCode }) => {
  if (!cities || Object.keys(cities).length === 0) {
    return (
      <Box sx={{ mt: 2, p: 1, bgcolor: theme.colors.background.tertiary, borderRadius: theme.borderRadius.sm }}>
        <Typography variant="caption" color="text.secondary">
          No city-level data available
        </Typography>
      </Box>
    );
  }

  const cityEntries = Object.entries(cities).slice(0, 5); // Show top 5 cities
  const hasMore = Object.keys(cities).length > 5;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 1, 
          color: theme.colors.text.primary,
          fontWeight: 'medium'
        }}
      >
        Regional Breakdown
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {cityEntries.map(([cityName, cityData]) => (
          <Box 
            key={`${countryCode}-${cityName}`}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 0.5,
              bgcolor: theme.colors.background.secondary,
              borderRadius: theme.borderRadius.sm,
              fontSize: '0.8rem'
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, color: theme.colors.text.primary }}>
              {cityName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {cityData.total || 0}
              </Typography>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: getStatusColor(cityData.in_progress_percentage || 0)
                }}
              />
            </Box>
          </Box>
        ))}
        {hasMore && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.colors.text.muted,
              textAlign: 'center',
              mt: 0.5,
              fontStyle: 'italic'
            }}
          >
            +{Object.keys(cities).length - 5} more cities
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Helper function to get status color based on in-progress percentage
 */
const getStatusColor = (inProgressPercentage) => {
  if (inProgressPercentage >= 75) return theme.colors.semantic.error; // Red for high in-progress
  if (inProgressPercentage >= 50) return theme.colors.semantic.warning; // Orange for medium
  if (inProgressPercentage >= 25) return '#eab308'; // Yellow for low-medium
  return theme.colors.semantic.success; // Green for low in-progress
};

/**
 * CountryDetailPopup Component
 * Enhanced popup for country markers with statistics, trends, and regional breakdown
 */
const CountryDetailPopup = ({ 
  countryData, 
  zoomLevel = 5, 
  maxWidth = 400,
  showRegionalBreakdown = true 
}) => {
  if (!countryData) {
    return null;
  }

  const {
    country_name,
    country_code,
    open_count = 0,
    total_count = 0,
    in_progress_percentage = 0,
    cities = {}
  } = countryData;

  // Calculate trend (placeholder - would need historical data for real trends)
  const getTrendIndicator = (percentage) => {
    if (percentage > 60) return '+High';
    if (percentage > 30) return '~Stable';
    return '-Low';
  };

  const trend = getTrendIndicator(in_progress_percentage);
  const completedCount = total_count - open_count;
  const completionRate = total_count > 0 ? ((completedCount / total_count) * 100).toFixed(1) : 0;

  return (
    <Popup 
      className="enhanced-popup" 
      maxWidth={maxWidth}
      closeButton={true}
      autoClose={false}
      closeOnEscapeKey={true}
    >
      <Box sx={{ p: 0, minWidth: '280px', maxWidth: `${maxWidth}px` }}>
        {/* Header */}
        <Box 
          sx={{ 
            p: 2, 
            pb: 1,
            borderBottom: `1px solid ${theme.colors.border.light}`,
            bgcolor: theme.colors.background.primary
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.colors.text.primary,
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {country_name}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: getStatusColor(in_progress_percentage),
                flexShrink: 0
              }}
            />
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.colors.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {country_code} • {in_progress_percentage.toFixed(1)}% In Progress
          </Typography>
        </Box>

        {/* Statistics Grid */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 1.5,
              mb: 2
            }}
          >
            <StatCard 
              label="Open Tickets" 
              value={open_count} 
              color="error"
              trend={trend}
            />
            <StatCard 
              label="Total Tickets" 
              value={total_count} 
              color="primary"
            />
            <StatCard 
              label="Completed" 
              value={`${completionRate}%`} 
              color="success"
            />
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 0.5
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Progress Status
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                {open_count} / {total_count}
              </Typography>
            </Box>
            <Box 
              sx={{ 
                height: 8, 
                bgcolor: theme.colors.neutral[200], 
                borderRadius: theme.borderRadius.full,
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  height: '100%', 
                  width: `${in_progress_percentage}%`,
                  bgcolor: getStatusColor(in_progress_percentage),
                  transition: 'width 0.3s ease-in-out'
                }} 
              />
            </Box>
          </Box>
        </Box>

        {/* Regional Breakdown (shown when zoomed in) */}
        {showRegionalBreakdown && zoomLevel >= 6 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <RegionalBreakdown cities={cities} countryCode={country_code} />
          </Box>
        )}

        {/* Footer with additional info */}
        {/* <Box 
          sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: theme.colors.background.tertiary,
            borderTop: `1px solid ${theme.colors.border.light}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="caption" color="text.muted">
            {Object.keys(cities).length} cities
          </Typography>
          <Typography variant="caption" color="text.muted">
            Click for details
          </Typography>
        </Box> */}
      </Box>
    </Popup>
  );
};

CountryDetailPopup.propTypes = {
  countryData: PropTypes.shape({
    country_name: PropTypes.string.isRequired,
    country_code: PropTypes.string.isRequired,
    open_count: PropTypes.number,
    total_count: PropTypes.number,
    in_progress_percentage: PropTypes.number,
    cities: PropTypes.object
  }).isRequired,
  zoomLevel: PropTypes.number,
  maxWidth: PropTypes.number,
  showRegionalBreakdown: PropTypes.bool
};

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'error']),
  trend: PropTypes.string
};

RegionalBreakdown.propTypes = {
  cities: PropTypes.object,
  countryCode: PropTypes.string.isRequired
};

export default CountryDetailPopup;